import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// ============================================
// STYLED COMPONENTS
// ============================================

const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
`;

const Header = styled.div`
  padding: 20px 30px;
  background: rgba(30, 40, 60, 0.8);
  border-bottom: 1px solid rgba(100, 150, 200, 0.2);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  
  span.badge {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

const Toolbox = styled.div`
  width: 280px;
  background: rgba(30, 40, 60, 0.95);
  border-right: 1px solid rgba(100, 150, 200, 0.2);
  padding: 20px;
  overflow-y: auto;
`;

const ToolboxSection = styled.div`
  margin-bottom: 24px;
`;

const ToolboxTitle = styled.h3`
  color: rgba(255, 255, 255, 0.6);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 0 0 12px 0;
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(102, 126, 234, 0); }
  100% { box-shadow: 0 0 0 0 rgba(102, 126, 234, 0); }
`;

const BlockTemplate = styled.div`
  background: ${props => props.color || '#667eea'};
  padding: 14px 16px;
  border-radius: 8px;
  margin-bottom: 10px;
  cursor: grab;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  transition: all 0.2s ease;
  
  /* Puzzle notch on top */
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 20px;
    width: 20px;
    height: 6px;
    background: ${props => props.color || '#667eea'};
    border-radius: 4px 4px 0 0;
  }
  
  /* Puzzle indent on bottom */
  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 20px;
    width: 20px;
    height: 6px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 0 0 4px 4px;
  }
  
  &:hover {
    transform: scale(1.02);
    animation: ${pulse} 1s infinite;
  }
  
  &:active {
    cursor: grabbing;
  }
`;

const Canvas = styled.div`
  flex: 1;
  padding: 40px;
  overflow: auto;
  position: relative;
`;

const CanvasInner = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 20px;
  background: rgba(30, 40, 60, 0.3);
  border-radius: 16px;
  border: 2px dashed rgba(100, 150, 200, 0.2);
  min-width: 600px;
`;

const DropZone = styled.div`
  min-height: 80px;
  border: 2px dashed rgba(100, 150, 200, 0.3);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  transition: all 0.2s ease;
  
  ${props => props.active && `
    border-color: #667eea;
    background: rgba(102, 126, 234, 0.1);
    color: #667eea;
  `}
`;

const Block = styled.div`
  background: ${props => props.color || '#667eea'};
  padding: 16px 20px;
  border-radius: 10px;
  color: #fff;
  position: relative;
  margin-bottom: 0;
  cursor: move;
  transition: all 0.2s ease;
  
  /* Puzzle notch on top */
  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 24px;
    width: 24px;
    height: 8px;
    background: ${props => props.color || '#667eea'};
    border-radius: 6px 6px 0 0;
  }
  
  /* Puzzle indent on bottom */
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 24px;
    width: 24px;
    height: 8px;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 0 0 6px 6px;
  }
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  }
  
  &:first-child::before {
    display: none;
  }
`;

const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 8px;
`;

const BlockIcon = styled.span`
  font-size: 18px;
`;

const BlockContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BlockInput = styled.input`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 8px 12px;
  color: #fff;
  font-size: 13px;
  width: 100%;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
  }
`;

const BlockSelect = styled.select`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 8px 12px;
  color: #fff;
  font-size: 13px;
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
  }
  
  option {
    background: #1e293b;
  }
`;

const NestedBlocks = styled.div`
  margin-left: 30px;
  margin-top: 12px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  border-left: 3px solid rgba(255, 255, 255, 0.2);
`;

const BranchLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.7;
  margin-bottom: 8px;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(239, 68, 68, 0.8);
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.2s;
  
  ${Block}:hover & {
    opacity: 1;
  }
`;

const PreviewPanel = styled.div`
  width: 300px;
  background: rgba(30, 40, 60, 0.95);
  border-left: 1px solid rgba(100, 150, 200, 0.2);
  padding: 20px;
  overflow-y: auto;
`;

const PreviewTitle = styled.h3`
  color: #fff;
  font-size: 16px;
  margin: 0 0 16px 0;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(100, 150, 200, 0.2);
`;

const PreviewCode = styled.pre`
  background: rgba(0, 0, 0, 0.3);
  padding: 16px;
  border-radius: 8px;
  color: #a5f3fc;
  font-size: 11px;
  overflow-x: auto;
  line-height: 1.5;
`;

// ============================================
// BLOCK TYPES
// ============================================

const BLOCK_TYPES = {
  start: { icon: '🚀', label: 'When conversation starts', color: '#22c55e' },
  question: { icon: '❓', label: 'Ask question', color: '#667eea' },
  ifCondition: { icon: '⚡', label: 'If condition', color: '#f59e0b' },
  requestDoc: { icon: '📄', label: 'Request document', color: '#06b6d4' },
  aiCheck: { icon: '🤖', label: 'AI analyzes', color: '#8b5cf6' },
  setOutcome: { icon: '✅', label: 'Set outcome', color: '#10b981' },
  failOutcome: { icon: '❌', label: 'Mark ineligible', color: '#ef4444' },
};

// ============================================
// INITIAL BLOCKS - Immigration Example
// ============================================

const initialBlocks = [
  {
    id: '1',
    type: 'start',
    data: { trigger: 'work_permit_request' },
  },
  {
    id: '2',
    type: 'question',
    data: { variable: 'worked_in_canada', text: 'Have you worked in Canada before?' },
  },
  {
    id: '3',
    type: 'ifCondition',
    data: { variable: 'worked_in_canada', operator: '==', value: 'yes' },
    thenBlocks: [
      {
        id: '4',
        type: 'question',
        data: { variable: 'job_type', text: 'What type of work did you do?' },
      },
      {
        id: '5',
        type: 'ifCondition',
        data: { variable: 'job_type', operator: 'contains', value: 'waiter' },
        thenBlocks: [
          {
            id: '6',
            type: 'requestDoc',
            data: { docType: 'salary_page', variable: 'salary_doc' },
          },
          {
            id: '7',
            type: 'aiCheck',
            data: { document: 'salary_doc', check: 'does NOT mention "officiant"', variable: 'doc_clean' },
          },
          {
            id: '8',
            type: 'ifCondition',
            data: { variable: 'doc_clean', operator: '==', value: 'true' },
            thenBlocks: [
              { id: '9', type: 'setOutcome', data: { outcome: 'Eligible as Kitchen Worker' } },
            ],
            elseBlocks: [
              { id: '10', type: 'failOutcome', data: { reason: 'Job title ineligible' } },
            ],
          },
        ],
        elseBlocks: [
          { id: '11', type: 'setOutcome', data: { outcome: 'Eligible - Good job type' } },
        ],
      },
    ],
    elseBlocks: [
      { id: '12', type: 'setOutcome', data: { outcome: 'Check education credentials instead' } },
    ],
  },
];

// ============================================
// MAIN COMPONENT
// ============================================

const FlowBuilderBlocks = () => {
  const [blocks, setBlocks] = useState(initialBlocks);
  const [draggedType, setDraggedType] = useState(null);
  const [activeDropZone, setActiveDropZone] = useState(null);

  const handleDragStart = (e, type) => {
    setDraggedType(type);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, zoneId) => {
    e.preventDefault();
    setActiveDropZone(zoneId);
  };

  const handleDragLeave = () => {
    setActiveDropZone(null);
  };

  const handleDrop = (e, parentId = null, branch = null) => {
    e.preventDefault();
    setActiveDropZone(null);
    
    if (!draggedType) return;

    const newBlock = {
      id: Date.now().toString(),
      type: draggedType,
      data: getDefaultData(draggedType),
      ...(draggedType === 'ifCondition' ? { thenBlocks: [], elseBlocks: [] } : {}),
    };

    if (parentId && branch) {
      // Add to nested blocks
      setBlocks(prev => addNestedBlock(prev, parentId, branch, newBlock));
    } else {
      // Add to root
      setBlocks(prev => [...prev, newBlock]);
    }
    
    setDraggedType(null);
  };

  const getDefaultData = (type) => {
    switch (type) {
      case 'question': return { variable: 'answer', text: 'Your question here...' };
      case 'ifCondition': return { variable: 'answer', operator: '==', value: 'yes' };
      case 'requestDoc': return { docType: 'document', variable: 'doc' };
      case 'aiCheck': return { document: 'doc', check: 'contains specific text', variable: 'result' };
      case 'setOutcome': return { outcome: 'Success!' };
      case 'failOutcome': return { reason: 'Ineligible' };
      case 'start': return { trigger: 'conversation_start' };
      default: return {};
    }
  };

  const addNestedBlock = (blocks, parentId, branch, newBlock) => {
    return blocks.map(block => {
      if (block.id === parentId) {
        return {
          ...block,
          [branch]: [...(block[branch] || []), newBlock],
        };
      }
      if (block.thenBlocks) {
        return {
          ...block,
          thenBlocks: addNestedBlock(block.thenBlocks, parentId, branch, newBlock),
          elseBlocks: addNestedBlock(block.elseBlocks || [], parentId, branch, newBlock),
        };
      }
      return block;
    });
  };

  const deleteBlock = (blockId) => {
    const removeBlock = (blocks) => blocks.filter(b => {
      if (b.id === blockId) return false;
      if (b.thenBlocks) {
        b.thenBlocks = removeBlock(b.thenBlocks);
        b.elseBlocks = removeBlock(b.elseBlocks || []);
      }
      return true;
    });
    setBlocks(prev => removeBlock(prev));
  };

  const renderBlock = (block, depth = 0) => {
    const config = BLOCK_TYPES[block.type];
    
    return (
      <Block key={block.id} color={config.color} style={{ marginLeft: depth * 20 }}>
        <DeleteButton onClick={() => deleteBlock(block.id)}>×</DeleteButton>
        <BlockHeader>
          <BlockIcon>{config.icon}</BlockIcon>
          {config.label}
        </BlockHeader>
        <BlockContent>
          {block.type === 'question' && (
            <>
              <BlockInput 
                placeholder="Question text..." 
                defaultValue={block.data.text}
              />
              <BlockInput 
                placeholder="Save answer as..." 
                defaultValue={block.data.variable}
                style={{ width: '50%' }}
              />
            </>
          )}
          {block.type === 'ifCondition' && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <BlockInput 
                placeholder="variable" 
                defaultValue={block.data.variable}
                style={{ width: '100px' }}
              />
              <BlockSelect defaultValue={block.data.operator}>
                <option value="==">equals</option>
                <option value="!=">not equals</option>
                <option value="contains">contains</option>
              </BlockSelect>
              <BlockInput 
                placeholder="value" 
                defaultValue={block.data.value}
                style={{ width: '100px' }}
              />
            </div>
          )}
          {block.type === 'requestDoc' && (
            <BlockInput 
              placeholder="Document type..." 
              defaultValue={block.data.docType}
            />
          )}
          {block.type === 'aiCheck' && (
            <BlockInput 
              placeholder="AI should check for..." 
              defaultValue={block.data.check}
            />
          )}
          {block.type === 'setOutcome' && (
            <BlockInput 
              placeholder="Outcome message..." 
              defaultValue={block.data.outcome}
            />
          )}
          {block.type === 'failOutcome' && (
            <BlockInput 
              placeholder="Failure reason..." 
              defaultValue={block.data.reason}
            />
          )}
        </BlockContent>
        
        {block.type === 'ifCondition' && (
          <>
            <NestedBlocks>
              <BranchLabel>✓ Then do:</BranchLabel>
              {block.thenBlocks?.map(b => renderBlock(b, 0))}
              <DropZone
                active={activeDropZone === `${block.id}-then`}
                onDragOver={(e) => handleDragOver(e, `${block.id}-then`)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, block.id, 'thenBlocks')}
              >
                Drop blocks here
              </DropZone>
            </NestedBlocks>
            <NestedBlocks>
              <BranchLabel>✗ Else do:</BranchLabel>
              {block.elseBlocks?.map(b => renderBlock(b, 0))}
              <DropZone
                active={activeDropZone === `${block.id}-else`}
                onDragOver={(e) => handleDragOver(e, `${block.id}-else`)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, block.id, 'elseBlocks')}
              >
                Drop blocks here
              </DropZone>
            </NestedBlocks>
          </>
        )}
      </Block>
    );
  };

  const generateJSON = () => {
    return JSON.stringify(blocks, null, 2);
  };

  return (
    <PageContainer>
      <Header>
        <Title>
          🧩 Scenario Block Builder
          <span className="badge">Option B: Blockly-Style</span>
        </Title>
      </Header>
      
      <ContentArea>
        <Toolbox>
          <ToolboxSection>
            <ToolboxTitle>Control</ToolboxTitle>
            <BlockTemplate
              color={BLOCK_TYPES.start.color}
              draggable
              onDragStart={(e) => handleDragStart(e, 'start')}
            >
              {BLOCK_TYPES.start.icon} {BLOCK_TYPES.start.label}
            </BlockTemplate>
            <BlockTemplate
              color={BLOCK_TYPES.ifCondition.color}
              draggable
              onDragStart={(e) => handleDragStart(e, 'ifCondition')}
            >
              {BLOCK_TYPES.ifCondition.icon} {BLOCK_TYPES.ifCondition.label}
            </BlockTemplate>
          </ToolboxSection>
          
          <ToolboxSection>
            <ToolboxTitle>Interaction</ToolboxTitle>
            <BlockTemplate
              color={BLOCK_TYPES.question.color}
              draggable
              onDragStart={(e) => handleDragStart(e, 'question')}
            >
              {BLOCK_TYPES.question.icon} {BLOCK_TYPES.question.label}
            </BlockTemplate>
            <BlockTemplate
              color={BLOCK_TYPES.requestDoc.color}
              draggable
              onDragStart={(e) => handleDragStart(e, 'requestDoc')}
            >
              {BLOCK_TYPES.requestDoc.icon} {BLOCK_TYPES.requestDoc.label}
            </BlockTemplate>
            <BlockTemplate
              color={BLOCK_TYPES.aiCheck.color}
              draggable
              onDragStart={(e) => handleDragStart(e, 'aiCheck')}
            >
              {BLOCK_TYPES.aiCheck.icon} {BLOCK_TYPES.aiCheck.label}
            </BlockTemplate>
          </ToolboxSection>
          
          <ToolboxSection>
            <ToolboxTitle>Outcomes</ToolboxTitle>
            <BlockTemplate
              color={BLOCK_TYPES.setOutcome.color}
              draggable
              onDragStart={(e) => handleDragStart(e, 'setOutcome')}
            >
              {BLOCK_TYPES.setOutcome.icon} {BLOCK_TYPES.setOutcome.label}
            </BlockTemplate>
            <BlockTemplate
              color={BLOCK_TYPES.failOutcome.color}
              draggable
              onDragStart={(e) => handleDragStart(e, 'failOutcome')}
            >
              {BLOCK_TYPES.failOutcome.icon} {BLOCK_TYPES.failOutcome.label}
            </BlockTemplate>
          </ToolboxSection>
        </Toolbox>
        
        <Canvas>
          <CanvasInner>
            {blocks.map(block => renderBlock(block))}
            <DropZone
              active={activeDropZone === 'root'}
              onDragOver={(e) => handleDragOver(e, 'root')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e)}
            >
              📦 Drop new blocks here
            </DropZone>
          </CanvasInner>
        </Canvas>
        
        <PreviewPanel>
          <PreviewTitle>📋 Generated Structure</PreviewTitle>
          <PreviewCode>
            {generateJSON()}
          </PreviewCode>
        </PreviewPanel>
      </ContentArea>
    </PageContainer>
  );
};

export default FlowBuilderBlocks;



